/** @jsx jsx */
import React, { useEffect, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';
import Handlebars from 'handlebars';
import { useCustomer } from '../../../contexts/CustomerContext';
import { convertKeysToUnderscore } from '../../../templating/utils';

const camelCaseToSnakeCase = (obj) => {
  return Object.keys(obj).reduce((acc, key) => {
    const snakeKey = key.replace(/[A-Z]/g, (match, offset) => {
      return (offset > 0 ? '_' : '') + match.toLowerCase();
    });
    acc[snakeKey] = obj[key];
    return acc;
  }, {});
};

const useText = ({ background, foreground, str, pose, student }) => {
  if (str === undefined) return '';
  if (str === '') return str;

  const template = Handlebars.compile(str);

  let res = '';

  try {
    res = template({ background, foreground, student, pose });
  } catch {
    return str;
  }

  return res;
};

const HtmlElementChild = ({
  rootWidth,
  rootHeight,
  background,
  foreground,
  sheet,
  inBuilder = false,
  sheets = [],
  setSheets = () => { },
  containerWidth,
  htmlElement,
  htmlElements,
  student,
  pose,
}) => {
  const { styles: baseStyles, tag: Tag, data } = htmlElement;
  const ref = useRef();
  const [isEditable, setIsEditable] = useState(false);

  // convert hard pixel widths to percentages
  const getWidth = () => {
    if (!baseStyles.width) return undefined;
    if (!baseStyles.width.includes('px')) return baseStyles.width;

    return `${(parseFloat(baseStyles.width.replace('px', '')) / rootWidth) * 100}%`;
  };

  // convert hard pixel widths to percentages
  const getHeight = () => {
    if (!baseStyles.height) return undefined;
    if (!baseStyles.height.includes('px')) return baseStyles.height;

    return `${(parseFloat(baseStyles.height.replace('px', '')) / rootHeight) * 100}%`;
  };

  const styles = { ...baseStyles, width: getWidth(), height: getHeight() };

  const text = useText({ background, pose, student, str: data.innerText || '' });
  const src = useText({ background, pose, foreground, student, str: data.src || '' });

  const [textContent, setTextContent] = useState((sheet?.customerData || {})[htmlElement.title] || text);

  const fontSize = htmlElement.styles['font-size'];

  const onFocus = () => {
    if (!inBuilder) return;
    if (!htmlElement.editable) return;

    setIsEditable(true);
  };

  useEffect(() => {
    if (!isEditable) return;

    ref.current.focus();
  }, [isEditable]);

  const onBlur = () => {
    if (!htmlElement.editable) return;

    setIsEditable(false);
  };

  const onInput = (e) => {
    e.stopPropagation();

    if (!inBuilder) return;
    if (!htmlElement.editable) return;

    const newSheets = sheets.map((s) => {
      if (s.index !== sheet.index) return s;

      const s2 = { ...s, customerData: { ...s.customerData, [htmlElement.title]: e.target.textContent } };

      return s2;
    });

    setSheets(newSheets);
  };

  useEffect(() => {
    if (!ref.current) return;
    if (!fontSize) return;

    const container = document.querySelector('#component-container');

    const resize = () => {
      const currentWidth = container.clientWidth;
      const ratio = currentWidth / containerWidth;

      const parsedFontSize = parseFloat(fontSize.replace('px', ''));

      ref.current.style.fontSize = `${ratio * parsedFontSize}px`;
    };

    window.addEventListener('resize', resize);

    resize();

    return () => window.removeEventListener('resize', resize);
  }, [ref.current, fontSize]);

  if (Tag === 'img') {
    return (
      <Tag css={css`${styles}`} src={src} />
    );
  }

  const childEls = htmlElements.filter((el) => el.parent_id === htmlElement.id);

  return (
    <Tag
      onClick={onFocus}
      onBlur={onBlur}
      onInput={onInput}
      ref={ref}
      css={css`${styles}`}
      contentEditable={isEditable}
    >
      {textContent}
      {childEls.map((el) => (
        <HtmlElementChild
          rootWidth={rootWidth}
          rootHeight={rootHeight}
          foreground={foreground}
          sheet={sheet}
          background={background}
          setSheets={setSheets}
          sheets={sheets}
          inBuilder={inBuilder}
          key={el.id}
          htmlElement={el}
          htmlElements={htmlElements}
          student={student}
          pose={pose}
          containerWidth={containerWidth}
        />
      ))}
    </Tag>
  );
};

const MultiPoseComponent = ({ inBuilder = false, sheets = [], setSheets = () => { }, sheet }) => {
  const htmlElements = Object.values(sheet.imageType.component.htmlElements);
  const root = htmlElements.find((el) => el.parent_id === null && el.position === 1);
  const { customer } = useCustomer();

  const student = camelCaseToSnakeCase(customer.currentStudent);
  const pose = camelCaseToSnakeCase(sheet.pose);

  const getAspectRatio = () => {
    const width = root.styles.width.replace('px', '');
    const height = root.styles.height.replace('px', '');

    return `${width} / ${height}`;
  };

  const rootStyles = {
    ...root.styles,
    width: undefined,
    height: undefined,
    aspectRatio: getAspectRatio(),
  };

  const rootElement = { ...root, styles: rootStyles };

  return (
    <div id="component-container" style={{ fontWeight: 'normal', width: '100%' }}>
      <HtmlElementChild
        background={convertKeysToUnderscore(sheet.background || {})}
        foreground={convertKeysToUnderscore(sheet.foreground || {})}
        sheets={sheets}
        sheet={sheet}
        setSheets={setSheets}
        inBuilder={inBuilder}
        htmlElements={htmlElements}
        containerWidth={parseInt(root.styles.width.replace('px', ''), 10)}
        htmlElement={rootElement}
        student={student}
        pose={pose}
        rootWidth={parseFloat(root.styles.width.replace('px', ''))}
        rootHeight={parseFloat(root.styles.height.replace('px', ''))}
      />
    </div>
  );
};

export default MultiPoseComponent;
